import { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { formatGrosze } from '@/lib/format';
import { useKolejkaModeracji, useMenuDoModeracji } from '@/lib/useAnalytics';
import type { DanieDoModeracji } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * MENU → MODERACJA RĘCZNA. Kolejka dań czekających na decyzję.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ TRZY STANY, NIE DWA ───────────────────────────────────────────────────────────────────────┐
 * │ `IsConfirmed` w systemie głównym jest typu `bool?`:                                         │
 * │                                                                                             │
 * │     NULL   → OCZEKUJE      to jest kolejka moderacji                                        │
 * │     true   → ZATWIERDZONE                                                                   │
 * │     false  → ODRZUCONE                                                                      │
 * │                                                                                             │
 * │ Potraktowanie tego pola jak zwykłej wartości logicznej skleiłoby „oczekuje" z „odrzucone"   │
 * │ i kolejka przestałaby istnieć. Dlatego serwer podaje stan SŁOWEM, a nie `true/false/null`:  │
 * │ przez zwykłe sprawdzenie prawdziwości nie da się tego pomylić.                              │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ŚCIEŻKA RĘCZNA ZOSTAJE NA ZAWSZE. Nawet gdy dojdzie moderacja automatyczna, ten ekran ma
 * działać — automat, którego nie da się obejść ręcznie, zatrzymuje restaurację do czasu, aż
 * ktoś naprawi automat.
 */

const STANY = [
  // „Wymaga uwagi" stoi PIERWSZE i jest domyślne. Lista, na której 95% pozycji jest w porządku,
  // uczy przewijać bez czytania — i wtedy ta jedna groźna też zostanie przewinięta.
  { klucz: 'wyjatki', etykieta: 'Wymaga uwagi' },
  { klucz: 'oczekujace', etykieta: 'Wszystkie oczekujące' },
  { klucz: 'zatwierdzone', etykieta: 'Zatwierdzone' },
  { klucz: 'odrzucone', etykieta: 'Odrzucone' },
];

/**
 * Kody powodów są maszynowe i stabilne; tekst dla operatora powstaje z nich TUTAJ.
 * Rozdzielenie jest celowe: zmiana brzmienia komunikatu nie może unieważniać zapisów audytu.
 */
const OPIS_POWODU: Record<string, string> = {
  PRICE_FACTOR_10_DROP: 'cena spadła dziesięciokrotnie',
  PRICE_FACTOR_10_INCREASE: 'cena wzrosła dziesięciokrotnie',
  PRICE_EXTREME_PERCENT_DROP: 'skrajny spadek ceny',
  PRICE_EXTREME_PERCENT_INCREASE: 'skrajny wzrost ceny',
  PRICE_SUSPICIOUS_DECIMAL_SHIFT: 'wygląda na przesunięty przecinek',
  PRICE_SUSPICIOUS_ZERO_REMOVAL: 'usunięte zero w kwocie',
  PRICE_SUSPICIOUS_ZERO_ADDITION: 'dodane zero w kwocie',
  PRICE_DROPPED_TO_ZERO: 'cena spadła do zera',
  PRICE_OUTSIDE_DISH_HISTORY: 'cena poza dotychczasowym pasmem tego dania',
  PRICE_RAPID_REPEAT_EDIT: 'seria szybkich zmian ceny',
  PRICE_NO_BASELINE: 'brak ceny, z którą można porównać',
  PRICE_BASELINE_UNCONFIRMED: 'punkt odniesienia niepotwierdzony',
  TEXT_PROFANITY: 'wulgaryzm w treści',
  TEXT_INSULT: 'obraźliwe sformułowanie',
  TEXT_SEXUAL: 'treść o charakterze seksualnym',
  TEXT_SPAM: 'treść reklamowa',
  TEXT_GARBAGE: 'treść wygląda na bełkot',
  TEXT_EXTERNAL_LINK: 'adres zewnętrzny w treści',
  TEXT_REQUIRES_MODEL_REVIEW: 'wymaga oceny modelu',
  TEXT_MODEL_UNAVAILABLE: 'ocena modelu niedostępna',
  TEXT_EMPTY_REQUIRED_FIELD: 'brak nazwy dania',
};

const opisPowodu = (kod: string) => OPIS_POWODU[kod] ?? kod;

function Pole({ etykieta, wartosc }: { etykieta: string; wartosc: string | null }) {
  if (!wartosc) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
      <Typography variant="body2">{wartosc}</Typography>
    </Box>
  );
}

function KartaDania({ d, zapisPodlaczony }: { d: DanieDoModeracji; zapisPodlaczony: boolean }) {
  const oczekuje = d.stan === 'OCZEKUJE';
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle1">{d.nazwa ?? '(bez nazwy)'}</Typography>
              <Chip
                size="small"
                label={d.stan === 'OCZEKUJE' ? 'oczekuje' : d.stan === 'ZATWIERDZONE' ? 'zatwierdzone' : 'odrzucone'}
                color={d.stan === 'OCZEKUJE' ? 'warning' : d.stan === 'ZATWIERDZONE' ? 'success' : 'default'}
                variant={d.stan === 'OCZEKUJE' ? 'filled' : 'outlined'}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{d.restauracja}</Typography>

            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Pole etykieta="Opis" wartosc={d.opis} />
              <Pole etykieta="Składniki" wartosc={d.skladniki} />
            </Stack>
          </Box>

          <Stack spacing={1} sx={{ flex: '0 0 auto', minWidth: 160 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Cena</Typography>
              <Typography variant="h6">
                {/* Brak ceny to brak ceny — nie „0 zł". Danie bez ceny jest podejrzane samo w sobie. */}
                {d.cena_grosze === null
                  ? <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>nieznana</Typography>
                  : formatGrosze(d.cena_grosze)}
              </Typography>
            </Box>
            {d.ilosc !== null && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ilość</Typography>
                <Typography variant="body2">{d.ilosc}</Typography>
              </Box>
            )}
          </Stack>
        </Stack>

        {oczekuje && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {/*
              Przyciski są widoczne zawsze, ale wyłączone, gdy API systemu głównego nie jest
              podłączone. Przycisk, który wygląda na czynny i kończy się błędem, jest gorszy
              niż wyłączony z podanym powodem.
            */}
            <Tooltip title={zapisPodlaczony ? '' : 'Działania operacyjne nie są w tym środowisku podłączone.'}>
              <span>
                <Button size="small" variant="contained" disabled={!zapisPodlaczony}>Zatwierdź</Button>
              </span>
            </Tooltip>
            <Tooltip title={zapisPodlaczony ? '' : 'Działania operacyjne nie są w tym środowisku podłączone.'}>
              <span>
                <Button size="small" color="error" disabled={!zapisPodlaczony}>Odrzuć</Button>
              </span>
            </Tooltip>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function MenuModeracjaPage() {
  const [stan, setStan] = useState('wyjatki');
  const wyjatkiWidok = stan === 'wyjatki';

  const kolejka = useKolejkaModeracji();
  // Zapytanie o listę pełną wołamy tylko wtedy, gdy operator o nią poprosi.
  const lista = useMenuDoModeracji(wyjatkiWidok ? 'oczekujace' : stan);

  const { data, isLoading, error } = wyjatkiWidok
    ? { data: undefined, isLoading: kolejka.isLoading, error: kolejka.error }
    : lista;

  const dania = wyjatkiWidok ? [] : (lista.data?.dania ?? []);
  const podlaczone = (wyjatkiWidok ? kolejka.data?.zrodlo_podlaczone : lista.data?.zrodlo_podlaczone) ?? false;
  const wyjatki = kolejka.data?.wyjatki ?? [];

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Moderacja menu"
        opis="Dania zgłoszone przez restauracje, czekające na decyzję."
      />

      {data?.zaleznosc && (
        <Alert severity="warning">
          <strong>Kolejka moderacji nie ma jeszcze źródła.</strong> {data.zaleznosc.skutek}{' '}
          Brakuje: <code>{data.zaleznosc.czego_brak}</code> (strumień {data.zaleznosc.strumien}).
        </Alert>
      )}

      <Karta>
        <Tabs value={stan} onChange={(_, v) => setStan(v)} sx={{ px: 1 }}>
          {STANY.map((s) => <Tab key={s.klucz} value={s.klucz} label={s.etykieta} />)}
        </Tabs>
      </Karta>

      {isLoading && <LoadingState />}
      {error ? <ErrorState error={error} context="Menu — moderacja ręczna." /> : null}

      {/* ── WIDOK WYJĄTKÓW ─────────────────────────────────────────────────────────────────── */}
      {wyjatkiWidok && !isLoading && !error && (
        <>
          {kolejka.data && (
            <Alert severity="info">
              <strong>Tryb cienia.</strong> {kolejka.data.tryb_opis}{' '}
              {kolejka.data.podsumowanie && (
                <>
                  Zgłoszeń: {kolejka.data.podsumowanie.zgloszen} · wymaga uwagi:{' '}
                  {kolejka.data.podsumowanie.wyjatkow} · przeszłoby automatycznie:{' '}
                  {kolejka.data.podsumowanie.kandydatow_do_auto}.
                </>
              )}
            </Alert>
          )}

          {kolejka.data?.zaleznosc_historii && (
            <Alert severity="warning">
              <strong>Brak historii cen.</strong> {kolejka.data.zaleznosc_historii.skutek}{' '}
              Brakuje: <code>{kolejka.data.zaleznosc_historii.czego_brak}</code>{' '}
              (strumień {kolejka.data.zaleznosc_historii.strumien}).
            </Alert>
          )}

          {wyjatki.length === 0 && (
            <EmptyState
              title={podlaczone ? 'Nic nie wymaga uwagi' : 'Źródło nie jest podłączone'}
              description={podlaczone
                ? 'Silnik nie znalazł zgłoszeń wymagających decyzji człowieka.'
                : 'Pusta lista NIE znaczy tu, że nic nie czeka — znaczy, że Control Center jeszcze tego nie widzi.'}
            />
          )}

          <Stack spacing={1.5}>
            {wyjatki.map((w) => (
              <Card key={w.danie_id} variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }} useFlexGap>
                    <Typography variant="subtitle1">{w.nazwa ?? '(bez nazwy)'}</Typography>
                    <Chip size="small" color={w.ryzyko === 'BLOCK' ? 'error' : 'warning'}
                      label={w.ryzyko === 'BLOCK' ? 'wysokie ryzyko' : 'do sprawdzenia'} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{w.restauracja}</Typography>
                  </Stack>

                  <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Było</Typography>
                      <Typography variant="body2">
                        {w.cena_odniesienia_grosze === null
                          ? 'brak odniesienia'
                          : formatGrosze(w.cena_odniesienia_grosze)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Proponowane</Typography>
                      <Typography variant="body2">
                        {w.cena_grosze === null ? 'nieznana' : formatGrosze(w.cena_grosze)}
                      </Typography>
                    </Box>
                    {w.zmiana_procent !== null && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Zmiana</Typography>
                        <Typography variant="body2">{w.zmiana_procent}%</Typography>
                      </Box>
                    )}
                  </Stack>

                  {/* POWODY, nie samo „wstrzymane". Wstrzymanie cudzej zmiany wymaga uzasadnienia. */}
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {w.powody.map((k) => (
                      <Chip key={k} size="small" variant="outlined" label={opisPowodu(k)} />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      {/* ── ŚCIEŻKA RĘCZNA — zostaje dostępna na zawsze ────────────────────────────────────── */}
      {!wyjatkiWidok && !isLoading && !error && dania.length === 0 && (
        <EmptyState
          title={podlaczone ? 'Nic nie czeka na decyzję' : 'Źródło nie jest podłączone'}
          description={podlaczone
            ? 'W tym stanie nie ma dziś żadnego dania.'
            : 'Pusta lista NIE znaczy tu, że nic nie czeka — znaczy, że Control Center jeszcze tego nie widzi.'}
        />
      )}

      {!wyjatkiWidok && (
        <Stack spacing={1.5}>
          {dania.map((d) => (
            <KartaDania key={d.danie_id} d={d} zapisPodlaczony={lista.data?.zapis_podlaczony ?? false} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default MenuModeracjaPage;
